import React, { useContext, useMemo, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import Spinner from "../../components/Spinner";
import StarekContext from "../../context/StarekContext";
import ConfirmModal from "../../components/modals/ConfirmModal";
import EntityOperationBar from "../../components/EntityOperationBar";
import MaintenanceSchedulesCalendarView from "./MaintenanceSchedulesCalendarView";
import MaintenanceSchedulesListingView from "./MaintenanceSchedulesListingView";
import AddMaintenanceScheduleSidebar from "./AddMaintenanceScheduleSidebar";

const MaintenanceSchedules = (props) => {
    const starekContext = useContext(StarekContext);
    const [viewMode, setViewMode] = useState("calendar");
    const [selectedRows, setSelectedRows] = useState([]);
    const [filterText, setFilterText] = useState("");
    const [toggleCleared, setToggleCleared] = useState(false);
    const [showSidebar, setShowSidebar] = useState(false);
    const [scheduleToEdit, setScheduleToEdit] = useState(null);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);

    const GET_DEVICES_IN_PLANT = gql`
        query GetDevicesForPlant($where: ShadowDeviceWhereInput) {
            shadowDevices(where: $where) {
                id
                name
                maintenance_schedules {
                    id
                    name
                    schedule_type
                    schedule_attributes
                    created_at
                    updated_at
                    created_by {
                        id
                        name
                    }
                    updated_by {
                        id
                        name
                    }
                }
            }
        }
    `;

    const plantId = starekContext.lastUsedPlant?.id;
    const where = useMemo(
        () => (plantId ? { plant_id: { equals: Number(plantId) } } : undefined),
        [plantId]
    );

    const { data, loading, error, refetch } = useQuery(GET_DEVICES_IN_PLANT, {
        variables: { where },
        client: starekContext.engineClient,
        fetchPolicy: "network-only",
    });

    const devices = data?.shadowDevices || [];

    const handleAdd = () => {
        setShowSidebar(true);
    };

    const handleEdit = () => {
        if (selectedRows.length !== 1) return;
        const selected = selectedRows[0];
        // Add device_id to schedule so the sidebar can pre-populate the device
        setScheduleToEdit({
            ...selected.schedule,
            device_id: selected.deviceId,
        });
        setShowSidebar(true);
    };

    const handleBulkDelete = async () => {
        setIsDeleting(true);
        setDeleteDone(0);
        const lastUsedWorkspace = starekContext?.lastUsedWorkspace;
        const token = starekContext?.auth;
        const { host, scheme, port } = lastUsedWorkspace?.engine || {};
        const apiUrl = `${scheme}://${host}:${port}/api/v1/${lastUsedWorkspace.name}/graphql`;

        try {
            let completed = 0;
            for (const row of selectedRows) {
                const query = {
                    query: `
                        mutation DeleteOneMaintenanceSchedule {
                            deleteOneMaintenanceSchedule(where: { id: ${row.scheduleId} }) { id }
                        }
                    `,
                };
                await fetch(apiUrl, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(query),
                });
                completed++;
                setDeleteDone(completed);
            }
            await refetch();
            setSelectedRows([]);
            setToggleCleared(!toggleCleared);
            setConfirmCfg({ open: false });
        } catch (error) {
            console.error("Error deleting schedules:", error);
            alert("Failed to delete some schedules. Please try again.");
        } finally {
            setIsDeleting(false);
            setDeleteDone(0);
        }
    };

    const handleSidebarClose = async () => {
        setShowSidebar(false);
        setScheduleToEdit(null);
        await refetch();
    };

    return loading ? (
        <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ minHeight: "100vh" }}
        >
            <div className="text-xs d-flex justify-content-center">
                <i className="fa fa-cogs fa-10x text-success"></i>
            </div>
            <Spinner />
        </div>
    ) : error ? (
        <div className="tw-text-center tw-mt-10 tw-text-red-600">
            Failed to load maintenance schedules: {error.message}
        </div>
    ) : (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">Maintenance Schedules</div>

            <EntityOperationBar
                entity={{
                    singular: "Maintenance Schedule",
                    plural: "Maintenance Schedules",
                }}
                allowSearch={true}
                allowAdd={true}
                allowDelete={true}
                allowEdit={true}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                deleteDone={deleteDone}
                onSearch={(text) => setFilterText(text)}
                onAdd={handleAdd}
                onViewChange={(mode) => {
                    setViewMode(mode);
                    setSelectedRows([]);
                }}
                onEdit={handleEdit}
                onDelete={handleBulkDelete}
            >
                <EntityOperationBar.View
                    mode="calendar"
                    icon="fa fa-calendar"
                    tooltip="Calendar View"
                >
                    <MaintenanceSchedulesCalendarView
                        devices={devices}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={(state) =>
                            setSelectedRows(state.selectedRows)
                        }
                        onRefresh={refetch}
                        filterText={filterText}
                    />
                </EntityOperationBar.View>

                <EntityOperationBar.View
                    mode="list"
                    icon="fa fa-list"
                    tooltip="List View"
                >
                    <MaintenanceSchedulesListingView
                        items={devices}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={(state) =>
                            setSelectedRows(state.selectedRows)
                        }
                        filterText={filterText}
                        toggleCleared={toggleCleared}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            <AddMaintenanceScheduleSidebar
                isOpen={showSidebar}
                onClose={handleSidebarClose}
                onCreated={handleSidebarClose}
                devices={devices}
                type={scheduleToEdit ? "edit" : "add"}
                schedule={scheduleToEdit}
            />
            <ConfirmModal {...confirmCfg} />
        </div>
    );
};

export { MaintenanceSchedules as Maintenance };
